/**
 * SignersCard — who signs a template, in order (multi-signer, Plus and Business plans).
 *
 * Signer 1 is always the order's customer. Up to two of the merchant's own
 * people can sign after them. On Pro it shows an upgrade prompt instead.
 */
import { useState } from 'react';
import { Banner, BlockStack, Box, Button, Card, InlineGrid, InlineStack, Text, TextField } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { MAX_SIGNERS, signerColor, type MultiSignerAccess } from '../multiSigner';
import { MULTI_SIGNER_PLAN } from '../plans';
import type { TemplateDetail, TemplateSigner } from '../types';

interface Draft { id?: string; role: string; name: string; email: string }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Dot({ position }: { position: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 11,
      background: signerColor(position), color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
      {position}
    </span>
  );
}

const toDraft = (s: TemplateSigner): Draft => ({ id: s.id, role: s.role, name: s.name, email: s.email });

export default function SignersCard({ template, access, onSaved, onSetDraft }: {
  template: TemplateDetail;
  access: MultiSignerAccess;
  onSaved: (t: TemplateDetail) => void;
  onSetDraft: () => void;
}) {
  const { request, startSubscription } = useApi();
  const [drafts, setDrafts] = useState<Draft[]>((template.signers ?? []).map(toDraft));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  if (!access.available) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text variant="headingSm" as="h2">Signers</Text>
          <Banner
            tone="info"
            title="Multiple signers is included in SignShield Plus and Business"
            action={{ content: `Upgrade to ${MULTI_SIGNER_PLAN.name.replace('SignShield ', '')} — $${MULTI_SIGNER_PLAN.price}/month`, loading: upgrading, onAction: async () => {
              setUpgrading(true);
              try { await startSubscription(MULTI_SIGNER_PLAN.key); } catch { setUpgrading(false); }
            } }}
          >
            <p>Have up to 3 people sign one document in order — for example your customer first, then you countersign.
              {' '}{MULTI_SIGNER_PLAN.name} includes {MULTI_SIGNER_PLAN.monthlyLimit} signing requests a month.</p>
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  const locked = template.status === 'ACTIVE';
  const dirty = JSON.stringify(drafts) !== JSON.stringify((template.signers ?? []).map(toDraft));
  const problems = drafts.map((d) =>
    !d.name.trim() ? 'Enter a name' : !EMAIL_RE.test(d.email.trim()) ? 'Enter a valid email' : null);
  const update = (i: number, patch: Partial<Draft>) => {
    setSaved(false);
    setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  };

  const save = async () => {
    if (problems.some(Boolean)) return;
    setSaving(true); setError(null); setSaved(false);
    try {
      const updated = await request<TemplateDetail>(`/templates/${template.id}/signers`, {
        method: 'PUT',
        body: JSON.stringify({ signers: drafts.map((d) => ({
          ...(d.id ? { id: d.id } : {}),
          role: d.role.trim() || 'Signer',
          name: d.name.trim(),
          email: d.email.trim(),
        })) }),
      });
      setDrafts((updated.signers ?? []).map(toDraft));
      onSaved(updated);
      setSaved(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Active template: a calm read-only list. Only the signer list is locked —
  // fields and settings still save normally — so don't make the page look locked.
  if (locked) {
    return (
      <Card>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text variant="headingSm" as="h2">Signers</Text>
            <Text as="p" tone="subdued">Who signs this document, in order. Each signer gets their own link after the previous one has signed.</Text>
          </BlockStack>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Dot position={1} />
            <Text as="span"><Text as="span" fontWeight="semibold">Customer</Text>{' '}
              <Text as="span" tone="subdued">· name and email from the order</Text></Text>
          </InlineStack>
          {(template.signers ?? []).map((s) => (
            <InlineStack key={s.id} gap="300" blockAlign="center" wrap={false}>
              <Dot position={s.position} />
              <Text as="span"><Text as="span" fontWeight="semibold">{s.name}</Text>{' '}
                <Text as="span" tone="subdued">· {s.role} · {s.email}</Text></Text>
            </InlineStack>
          ))}
          <InlineStack gap="200" blockAlign="center">
            <Text as="p" variant="bodySm" tone="subdued">
              This template is active. You can still change and save its fields and settings below.
            </Text>
            <Button variant="plain" onClick={() => setShowHowTo((v) => !v)}>Change signers</Button>
          </InlineStack>
          {showHowTo && (
            <Banner tone="info" action={{ content: 'Set to draft', onAction: onSetDraft }} onDismiss={() => setShowHowTo(false)}>
              <p>
                Signers can't be changed while the template is active. To change them: 1) click <strong>Set to draft</strong>,
                2) edit the signers and click <strong>Save signers</strong>, 3) click <strong>Activate</strong> again.
                Documents already sent are not affected.
              </p>
            </Banner>
          )}
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text variant="headingSm" as="h2">Signers</Text>
          <Text as="p" tone="subdued">
            Who signs this document, in order. Each signer gets their own link after the previous one has signed.
          </Text>
        </BlockStack>

        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

        <InlineStack gap="300" blockAlign="center" wrap={false}>
          <Dot position={1} />
          <BlockStack gap="0">
            <Text as="span" fontWeight="semibold">Customer</Text>
            <Text as="span" variant="bodySm" tone="subdued">Name and email come from the order automatically</Text>
          </BlockStack>
        </InlineStack>

        {drafts.map((d, i) => (
          <Box key={d.id ?? `new-${i}`} paddingInlineStart="0">
            <InlineStack gap="300" blockAlign="start" wrap={false}>
              <Box paddingBlockStart="600"><Dot position={i + 2} /></Box>
              <div style={{ flex: 1 }}>
                <InlineGrid columns={{ xs: 1, md: 3 }} gap="200">
                  <TextField label="Role" placeholder="e.g. Company representative" value={d.role}
                    onChange={(v) => update(i, { role: v })} autoComplete="off" disabled={locked} maxLength={60} />
                  <TextField label="Name" value={d.name} onChange={(v) => update(i, { name: v })} autoComplete="off"
                    disabled={locked} maxLength={120} error={dirty && d.name !== undefined && problems[i] === 'Enter a name' ? problems[i]! : undefined} />
                  <TextField label="Email" type="email" value={d.email} onChange={(v) => update(i, { email: v })} autoComplete="off"
                    disabled={locked} maxLength={254} error={dirty && d.email && problems[i] === 'Enter a valid email' ? problems[i]! : undefined} />
                </InlineGrid>
              </div>
              <Box paddingBlockStart="600">
                <Button variant="plain" tone="critical" disabled={locked}
                  onClick={() => { setSaved(false); setDrafts((prev) => prev.filter((_, j) => j !== i)); }}>
                  Remove
                </Button>
              </Box>
            </InlineStack>
          </Box>
        ))}

        <InlineStack align="space-between" blockAlign="center">
          <Button disabled={locked || drafts.length >= MAX_SIGNERS - 1}
            onClick={() => { setSaved(false); setDrafts((prev) => [...prev, { role: '', name: '', email: '' }]); }}>
            + Add signer
          </Button>
          <InlineStack gap="200" blockAlign="center">
            {saved && !dirty && <Text as="span" tone="success">Saved</Text>}
            <Button variant="primary" loading={saving} disabled={locked || !dirty || problems.some(Boolean)} onClick={save}>
              Save signers
            </Button>
          </InlineStack>
        </InlineStack>
        {drafts.length > 0 && (
          <Text as="p" variant="bodySm" tone="subdued">
            Next, assign fields to each signer {template.sourceType === 'EDITOR' ? 'in the text editor' : 'in Field placement'}.
            Removing a signer moves their fields back to the customer.
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}
